import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface PastEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  imageUrl: string;
  highlights: string;
}

export default function PastEvents() {
  const [pastEvents, setPastEvents] = useState<PastEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'news_section_past_events'));
      const data: PastEvent[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as PastEvent);
      });
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setPastEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Past Events — Kottayil Temple" description="Past events, festivals, and celebrations at Kottayil Temple (Kotta-Kovilakam Kshethram). Photos and memories from Muchukunnu." path="/news/past" keywords="Kottayil temple past events, previous festivals, event gallery, Muchukunnu celebrations, കൊട്ടയിൽ കഴിഞ്ഞ പരിപാടികൾ" />
      <PageBanner title="Past Events" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #4c1d95 0%, #a855f7 40%, #d8b4fe 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <HistoryIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Past Events Gallery
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Relive the beautiful moments from our celebrations
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Browse through our past celebrations, festivals, and community events.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<HistoryIcon sx={{ fontSize: '16px !important', color: '#a855f7 !important' }} />} label="Event Archives" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#4c1d95', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<PhotoLibraryIcon sx={{ fontSize: '16px !important', color: '#d8b4fe !important' }} />} label="Photo Memories" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: GOLD }} />
              </Box>
            ) : pastEvents.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 4, border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#aaa' }}>No past events recorded yet.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {pastEvents.map((event, idx) => {
                  const cardGradients = [['#a855f7','#7c3aed'], ['#3b82f6','#2563eb'], ['#f59e0b','#d97706'], ['#22c55e','#16a34a'], ['#ef4444','#dc2626'], ['#ec4899','#db2777']];
                  const g = cardGradients[idx % cardGradients.length];
                  return (
                    <Grid item xs={12} sm={6} key={event.id}>
                      <Box sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        height: '100%', display: 'flex', flexDirection: 'column',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 48px rgba(0,0,0,0.1)' },
                      }}>
                        {event.imageUrl && (
                          <Box component="img" src={event.imageUrl} alt={event.title}
                            sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                          />
                        )}
                        {/* Gradient header */}
                        <Box sx={{ background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                            <HistoryIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff', flex: 1, lineHeight: 1.3 }}>
                            {event.title}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <CalendarMonthIcon sx={{ fontSize: 12, color: g[0] }} />
                              <Typography sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>
                                {new Date(event.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </Typography>
                            </Box>
                            {event.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <PlaceIcon sx={{ fontSize: 12, color: '#ccc' }} />
                                <Typography sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>{event.location}</Typography>
                              </Box>
                            )}
                          </Box>
                          {event.highlights && (
                            <Box sx={{ mb: 1.5, p: 1.5, background: `linear-gradient(135deg, ${g[0]}08, ${g[1]}08)`, borderRadius: '14px', border: `1px solid ${g[0]}15` }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 12, color: g[0] }} />
                                <Typography sx={{ fontSize: '0.62rem', color: g[0], fontWeight: 800, textTransform: 'uppercase' }}>Highlights</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.74rem', color: '#777', lineHeight: 1.6 }}>
                                {event.highlights}
                              </Typography>
                            </Box>
                          )}
                          <Typography sx={{ fontSize: '0.78rem', color: '#777', lineHeight: 1.9, flex: 1 }}>
                            {event.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* DARK SLATE FOOTER */}
            <Box sx={{
              mt: 3, background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 },
              position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <HistoryIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>Cherishing Sacred Memories</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                  Every celebration strengthens our bond as a community of devotees.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="news" activePath="/news/past" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
